// @flow
import {
    AdSlot,
    Query,
    SeoData,
    StaticTable,
    TableGraphConnector,
} from '@haaretz/htz-components';
import React, { Fragment, } from 'react';
import { FelaComponent, FelaTheme, } from 'react-fela';

import type { StyleProps, } from '@haaretz/htz-css-tools';
import type { Node, } from 'react';

import PageRow from '../../components/PageRow/PageRow';
import RowItem from '../../components/RowItem/RowItem';
import MainLayout from '../../layouts/MainLayout';
import ExchangeQuery from '../queries/exchangeQuery.graphql';
import useAllAdUnits from '../../components/AdManager/AdUnitsProvider';
import useAdUnitPathTemplate, { createAdSlotProps, } from '../../components/AdManager/useAdUnitPathTemplate';

type Props = {
  url: {
    pathname: string,
    query: {
      section: string,
    },
    asPath: string,
  },
};

const numToString: (number | string) => string = num => (typeof num === 'number'
  ? num.toLocaleString('he', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  : num);

function exchange({
  url: {
    query: { section, },
    asPath,
  },
}: Props): Node {
  const isCryptoSection: boolean = section === 'crypto';

  const firstTableAssets = isCryptoSection
    ? [ 'BTC', 'LTC', 'ETH', 'ZEC', 'XRP', 'BCH', 'NEO', ]
    : [ '90012', '90041', '90011', '90032', '90031', '93021', '93022', '90052', '93052', ];

  const secondTableAssets = isCryptoSection
    ? [ 'ADA', 'CDT', 'GNT', 'MAID', 'STEEM', 'VTC', 'XMRG', ]
    : [ '9001', '9004', '9003', '9302', '9005', '9301', '9305', '9304', '9303', ];

  const title = isCryptoSection
    ? 'מטבעות דיגיטליים - TheMarker Finance'
    : 'מט"ח - מטבע חוץ - TheMarker Finance';

  const description = isCryptoSection
    ? 'כל המידע על  מטבעות דיגיטיליים: ביטקוין, לייטקוין, איתריום, ריפל, מונרו, Qtum  - נתונים בזמן אמת, גרפים חדשות ועוד באתר TheMarker Finance'
    : 'מטבעות חוץ :כל המידע על  מט"ח נתוני מסחר, נתונים בזמן אמת, גרפים חדשות ועוד באתר TheMarker Finance';

  const adUnits = useAllAdUnits();
  const adUnitPathTemplate = useAdUnitPathTemplate();
  const showBanner = false; // !!isCryptoSection;

  return (
    <MainLayout section={section}>
      <SeoData title={title} description={description} path={asPath} />
      <FelaTheme>
        {theme => (
          <Fragment>
            <PageRow miscStyles={{ marginBottom: [ { from: 'l', value: '0', }, ], }}>
              <RowItem title={isCryptoSection ? 'מטבעות עיקריים' : 'שערים רציפים'} showBanner={showBanner} isCrypto={isCryptoSection}>
                <FelaComponent
                  style={() => ({
                    extend: [ theme.mq({ from: 'l', }, { display: 'none', }), ],
                  })}
                >
                  <Query
                    query={ExchangeQuery}
                    variables={{
                      ids: firstTableAssets,
                    }}
                  >
                    {({ loading, error, data, }) => {
                      if (loading || error) return null;
                      const { assets = [], } = data || {};
                      return (
                        <StaticTable
                          data={assets}
                          linkContent
                          columns={[
                            {
                              title: 'שם נכס',
                              name: 'name',
                              styles: {
                                fontWeight: '700',
                                maxWidth: '17rem',
                                paddingStart: '2rem',
                              },
                              render: value => value || '-',
                            },
                            {
                              title: 'שער אחרון',
                              name: 'value',
                              styles: {
                                paddingStart: '2rem',
                              },
                              render: value => (value ? numToString(value) : '-'),
                            },
                            {
                              title: '% שינוי',
                              percentage: true,
                              name: 'changePercentage',
                              styles: function styles(value: number): StyleProps {
                                return {
                                  ...(value
                                    ? {
                                      color:
                                          value < 0
                                            ? theme.color('negative')
                                            : theme.color('positive'),
                                    }
                                    : {}),
                                  direction: 'ltr',
                                  fontWeight: '700',
                                  paddingStart: '2rem',
                                  position: 'relative',
                                  textAlign: 'start',
                                };
                              },
                              render: value => `
                                ${value > 0 ? '' : value < 0 ? '-' : ''}
                                ${value ? numToString(Math.abs(value)) : value}%
                              `,
                            },
                          ]}
                        />
                      );
                    }}
                  </Query>
                </FelaComponent>
                <TableGraphConnector
                  miscStyles={{ display: [ { until: 'l', value: 'none', }, ], }}
                  assetsId={firstTableAssets}
                  headers={[
                    {
                      display: 'שם נכס',
                      value: 'name',
                      style: {
                        paddingStart: '1rem',
                        paddingTop: '0.75rem',
                        paddingBottom: '0.75rem',
                        paddingEnd: '2rem',
                        marginBottom: '1rem',
                        backgroundColor: theme.color('neutral', '-6'),
                      },
                    },
                    {
                      display: 'שער אחרון',
                      value: 'value',
                      style: {
                        paddingEnd: '2rem',
                        paddingTop: '0.75rem',
                        paddingBottom: '0.75rem',
                        marginBottom: '1rem',
                        backgroundColor: theme.color('neutral', '-6'),
                      },
                    },
                    {
                      display: '% שינוי',
                      percentage: true,
                      value: 'changePercentage',
                      style: {
                        paddingEnd: '2rem',
                        paddingTop: '0.75rem',
                        paddingBottom: '0.75rem',
                        marginBottom: '1rem',
                        backgroundColor: theme.color('neutral', '-6'),
                      },
                    },
                  ]}
                />
              </RowItem>
            </PageRow>
            <PageRow
              miscStyles={{
                display: [ { until: 'l', value: 'none', }, ],
                paddingTop: '4rem',
                paddingBottom: '4rem',
                backgroundColor: theme.color('neutral', '-10'),
              }}
            >
              <RowItem>
                <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner1'], adUnitPathTemplate)} />
              </RowItem>
            </PageRow>
            <PageRow>
              <RowItem title={isCryptoSection ? 'מטבעות מתעוררים' : 'שערים יציגים'}>
                <FelaComponent
                  style={() => ({
                    extend: [ theme.mq({ from: 'l', }, { display: 'none', }), ],
                  })}
                >
                  <Query
                    query={ExchangeQuery}
                    variables={{
                      ids: secondTableAssets,
                    }}
                  >
                    {({ loading, error, data, }) => {
                      if (loading || error) return null;
                      const { assets = [], } = data || {};
                      return (
                        <StaticTable
                          data={assets}
                          linkContent
                          columns={[
                            {
                              title: 'שם נכס',
                              name: 'name',
                              styles: {
                                fontWeight: '700',
                                maxWidth: '17rem',
                                paddingStart: '2rem',
                              },
                              render: value => value || '-',
                            },
                            {
                              title: 'שער אחרון',
                              name: 'value',
                              styles: {
                                paddingStart: '2rem',
                              },
                              render: value => (value ? numToString(value) : '-'),
                            },
                            {
                              title: '% שינוי',
                              percentage: true,
                              name: 'changePercentage',
                              styles: function styles(value: number): StyleProps {
                                return {
                                  ...(value
                                    ? {
                                      color:
                                          value < 0
                                            ? theme.color('negative')
                                            : theme.color('positive'),
                                    }
                                    : {}),
                                  direction: 'ltr',
                                  fontWeight: '700',
                                  paddingStart: '2rem',
                                  position: 'relative',
                                  textAlign: 'start',
                                };
                              },
                              render: value => `
                                ${value > 0 ? '' : value < 0 ? '-' : ''}
                                ${value ? numToString(Math.abs(value)) : value}%
                              `,
                            },
                          ]}
                        />
                      );
                    }}
                  </Query>
                </FelaComponent>
                <TableGraphConnector
                  miscStyles={{ display: [ { until: 'l', value: 'none', }, ], }}
                  assetsId={secondTableAssets}
                  headers={[
                    {
                      display: 'שם נכס',
                      value: 'name',
                      style: {
                        paddingStart: '1rem',
                        paddingTop: '0.75rem',
                        paddingBottom: '0.75rem',
                        paddingEnd: '2rem',
                        marginBottom: '1rem',
                        backgroundColor: theme.color('neutral', '-6'),
                      },
                    },
                    {
                      display: 'שער אחרון',
                      value: 'value',
                      style: {
                        paddingEnd: '2rem',
                        paddingTop: '0.75rem',
                        paddingBottom: '0.75rem',
                        marginBottom: '1rem',
                        backgroundColor: theme.color('neutral', '-6'),
                      },
                    },
                    {
                      display: '% שינוי',
                      percentage: true,
                      value: 'changePercentage',
                      style: {
                        paddingEnd: '2rem',
                        paddingTop: '0.75rem',
                        paddingBottom: '0.75rem',
                        marginBottom: '1rem',
                        backgroundColor: theme.color('neutral', '-6'),
                      },
                    },
                  ]}
                />
              </RowItem>
            </PageRow>
            {!isCryptoSection ? (
              <PageRow>
                <RowItem>
                  <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner2'], adUnitPathTemplate)} />
                </RowItem>
              </PageRow>
            ) : null}
          </Fragment>
        )}
      </FelaTheme>
    </MainLayout>
  );
}

export default exchange;
